import React, { useEffect, useRef, useState } from 'react';

import { styled } from '@mui/material/styles';
import {
    Box,
    Card,
    Divider,
    Tab,
    TabPanel,
    Tabs,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import { debounce, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { DEBOUNCE_TIME } from 'common/constants';

import createProposalModal from '../createProposalModal';
import * as createProposalModalActions from '../createProposalModal/actions';
import duplicateProposalModal from '../duplicateProposalModal';
import * as duplicateProposalModalActions from '../duplicateProposalModal/actions';

import * as actions from './actions';
import Content from './components/Content';
import FilterAndCreate from './components/FilterAndCreate';
import * as selectors from './selectors';

export const TextFilterBy = styled(Typography)`
    font-size: 14px;
    font-style: normal;
    font-weight: bold;
    line-height: 17px;
    margin-bottom: 0;
`;

const Container = ({
    contactIsArchived,
    proposals,
    filterProposals,
    handleClickCreate,
    handleClickDuplicate,
    isFetchingProposals,
    isSavingProposal,
    projectId,
    reset,
    showEmptyState,
}) => {
    const { t } = useTranslation();
    const [filterData, setFilterData] = useState({
        projectId: projectId,
        status: 'active',
    });
    const [currentFilterData, setCurrentFilterData] = useState(filterData);

    useEffect(() => {
        filterProposals(filterData);
        return () => reset();
    }, []);

    useEffect(() => {
        if (!isEqual(currentFilterData, filterData)) {
            handleOnFilter(filterData);
            setCurrentFilterData(filterData);
        }
    }, [filterData]);

    const handleOnFilter = useRef(
        debounce((filter) => filterProposals(filter), DEBOUNCE_TIME),
    ).current;

    const handleOnSelectStatus = (value) => {
        const filter = { ...filterData, page: 0, status: value };
        setFilterData(filter);
    };

    const isFetching = isFetchingProposals || isSavingProposal;

    return (
        <Box>
            <Card sx={{ mt: 2 }}>
                <Card.Body>
                    <Box
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            alignItems: 'left',
                            display: 'flex',
                        }}
                    >
                        <Tabs centered value="proposals" variant="fullWidth">
                            <Tab
                                fullWidth
                                label={t('Proposal', { count: 2 })}
                                value="proposals"
                            />
                        </Tabs>
                    </Box>

                    <TabPanel selectedTab="proposals" value="proposals">
                        <FilterAndCreate
                            contactIsArchived={contactIsArchived}
                            filterData={filterData}
                            handleClickCreate={handleClickCreate}
                            handleOnSelectStatus={handleOnSelectStatus}
                            isFetching={isFetching}
                            projectId={projectId}
                        />

                        <Divider sx={{ my: 2 }} />

                        <Content
                            filterData={filterData}
                            handleClickCreate={handleClickCreate}
                            handleClickDuplicate={handleClickDuplicate}
                            isFetching={isFetching}
                            proposals={proposals}
                            showEmptyState={showEmptyState}
                        />
                    </TabPanel>
                </Card.Body>
            </Card>

            <createProposalModal.Container />

            <duplicateProposalModal.Container filterData={filterData} />
        </Box>
    );
};

Container.propTypes = {
    contactIsArchived: PropTypes.bool,
    proposals: PropTypes.array,
    filterProposals: PropTypes.func,
    handleClickCreate: PropTypes.func,
    handleClickDuplicate: PropTypes.func,
    isFetchingProposals: PropTypes.bool,
    isSavingProposal: PropTypes.bool,
    projectId: PropTypes.string,
    reset: PropTypes.func,
    showEmptyState: PropTypes.object,
};

const mapStateToProps = createStructuredSelector({
    isFetchingProposals: selectors.getIsFetchingProposals,
    proposals: selectors.getProposalsData,
    showEmptyState: selectors.getShowEmptyState,
});

const mapDispatchToProps = (dispatch) => ({
    filterProposals: (filterData) =>
        dispatch(actions.filterProposals(filterData)),
    handleClickCreate: (projectId) =>
        dispatch(createProposalModalActions.prepareCreate(projectId)),
    handleClickDuplicate: (values) =>
        dispatch(duplicateProposalModalActions.prepareDuplicate(values)),
    reset: () => dispatch(actions.reset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Container);
